const CORE_URL = import.meta.env.VITE_GUNSI_CORE_URL
  ?? `${location.protocol}//${location.hostname}:3000`

async function apiFetch(path, options = {}) {
  const res = await fetch(`${CORE_URL}${path}`, {
    credentials: 'include',
    headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : {},
    ...options,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })
  if (!res.ok) {
    // Errori "di dominio": il body porta un codice che la UI sa tradurre
    const detail = await res.json().catch(() => null)
    if (detail?.error) {
      const err = new Error(detail.error)
      err.code = detail.error
      err.detail = detail
      throw err
    }
    throw new Error(`${options.method ?? 'GET'} ${path} → ${res.status}`)
  }
  const text = await res.text()
  return text ? JSON.parse(text) : null
}

// Loads everything: settings, groups, expenses, settlements.
// Trade-off: simple, OK for first year of usage (low volume). Move to lazy
// per-group fetch later if a single user accumulates 1000s of expenses.
export function loadData() { return apiFetch('/even/data') }

export function saveSettings(settings) {
  return apiFetch('/even/settings', { method: 'PUT', body: settings })
}

// ── Groups ────────────────────────────────────────────────────────────────
export function createGroup(group)            { return apiFetch('/even/groups', { method: 'POST', body: group }) }
export function patchGroup(id, patch)         { return apiFetch(`/even/groups/${id}`, { method: 'PATCH', body: patch }) }
export function deleteGroup(id)               { return apiFetch(`/even/groups/${id}`, { method: 'DELETE' }) }

// ── Expenses ──────────────────────────────────────────────────────────────
export function createExpense(expense)        { return apiFetch('/even/expenses', { method: 'POST', body: expense }) }
export function patchExpense(id, patch)       { return apiFetch(`/even/expenses/${id}`, { method: 'PATCH', body: patch }) }
export function deleteExpense(id)             { return apiFetch(`/even/expenses/${id}`, { method: 'DELETE' }) }

// ── Settlements ───────────────────────────────────────────────────────────
export function createSettlement(s)           { return apiFetch('/even/settlements', { method: 'POST', body: s }) }
export function patchSettlement(id, patch)    { return apiFetch(`/even/settlements/${id}`, { method: 'PATCH', body: patch }) }
export function deleteSettlement(id)          { return apiFetch(`/even/settlements/${id}`, { method: 'DELETE' }) }

// ── Tally sync (opzione personale per-gruppo "quota su Tally") ────────────
export function setTallySync(groupId, enabled) {
  return apiFetch(`/even/groups/${groupId}/tally-sync`, { method: 'PUT', body: { enabled } })
}
export function backfillTallySync(groupId) {
  return apiFetch(`/even/groups/${groupId}/tally-sync/backfill`, { method: 'POST', body: {} })
}
export function wipeTallySync(groupId) {
  return apiFetch(`/even/groups/${groupId}/tally-sync/wipe`, { method: 'POST', body: {} })
}
// Opt-out personale per-spesa (solo l'utente loggato)
export function setExpenseTallyExcluded(expenseId, excluded) {
  return apiFetch(`/even/expenses/${expenseId}/tally-exclude`, { method: 'PUT', body: { excluded } })
}

// ── Bulk replace (LS → DB migration only) ─────────────────────────────────
export function replaceAllData({ groups, expenses, settlements }) {
  return apiFetch('/even/import', { method: 'POST', body: { groups, expenses, settlements } })
}

// ── User search (typeahead link member → utente Gunsi) ────────────────────
// Match per nome (partial) o email esatta. Ritorna { users: [{ id, name,
// image, hasApp }] } (hasApp presente solo se si passa app).
export function searchUsers(q, app) {
  const params = new URLSearchParams({ q })
  if (app) params.set('app', app)
  return apiFetch(`/api/users/search?${params}`)
}

// ── Shared currencies / FX (re-used from gunsi-core /currencies & /fx) ────
export function searchCurrencies(q, limit = 50) {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (limit) params.set('limit', String(limit))
  const qs = params.toString()
  return apiFetch(`/currencies/search${qs ? `?${qs}` : ''}`)
}

// Tassi EUR-based per una data (YYYY-MM-DD). Il server gestisce clamp a ieri
// e gap del cron: risponde sempre con i tassi più vicini ≤ data.
export function getFxRates(date) {
  return apiFetch(`/fx/rates/${date}`)
}

// (Il convertitore usa il componente condiviso CurrencyConverter di
// gunsi-auth, che fa le sue fetch /fx/* internamente.)
